import { useLayoutEffect, useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { useUnreadCount } from '../../hooks/useUnreadCount';
import { useBadgeCelebration } from '../../hooks/useBadgeCelebration';
import { useFinishSignup } from '../../hooks/useFinishSignup';
import BadgeCelebration from './BadgeCelebration';
import BottomNav from './BottomNav';
import TopNav from './TopNav';

const PAGE_BG = 'linear-gradient(165deg, var(--dark-bg-start) 0%, var(--dark-bg-mid) 40%, var(--dark-bg-end) 100%)';

// Disable browser scroll restoration — we manage it per-route below.
window.history.scrollRestoration = 'manual';

const AppShell = () => {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const { pathname } = useLocation();
  const scrollPositions = useRef({});
  const { count: unreadCount, refresh: refreshUnread } = useUnreadCount();
  const navigate = useNavigate();
  const { badges: newBadges, dismiss: dismissBadges } = useBadgeCelebration();
  // A new member's first signed-in screen: apply what sign-up left pending.
  useFinishSignup();

  // Lives in the shell so a badge earned elsewhere greets the member on
  // whatever screen they open next, not only on Profile.
  const celebration = newBadges?.length ? (
    <BadgeCelebration
      badges={newBadges}
      onClose={dismissBadges}
      onSeeAll={() => {
        dismissBadges();
        navigate('/profile');
      }}
    />
  ) : null;

  useLayoutEffect(() => {
    const positions = scrollPositions.current;
    // Restore saved position for this route, or start at top on first visit
    window.scrollTo(0, positions[pathname] ?? 0);

    // Save position when leaving this route
    return () => {
      positions[pathname] = window.scrollY;
    };
  }, [pathname]);

  // Nudge the badge on navigation; the hook's staleness guard keeps this from
  // being an RPC per route change.
  useEffect(() => {
    refreshUnread();
  }, [pathname, refreshUnread]);

  if (isDesktop) {
    return (
      // ⚠️ The page gradient lives HERE, on the full-width element — not on each
      // screen's root. Screen roots sit inside the max-w-[1088px] wrapper below,
      // so painting it there clipped it to the content column: the gradient
      // darkens toward the bottom while <body> stays flat #FAFAF7, leaving a
      // visible vertical seam at the cap. On a short screen like Explore that
      // reads as the content sitting in an off-centre box. Screens are
      // transparent and inherit this.
      <div className="min-h-screen" style={{ background: PAGE_BG }}>
        <TopNav unreadCount={unreadCount} />
        {/* The ONE desktop width cap. Screens must not re-apply their own
            max-width + mx-auto on top of this — doing so is a no-op that reads
            like an intentional constraint, which is how the feed stayed at
            1024px. TopNav's inner wrapper must match this value or the nav
            visibly misaligns with the content under it.

            1088 is the outer cap; px-6 inside it makes the CONTENT box 1040,
            which with a 320px sidebar and a 24px gap puts the main column at
            696px — a comfortable measure (~65-75 characters). Earlier tries at
            1200 and 1140 gave 808 and 728, both of which read as stretched
            with real post lengths. */}
        <div className="max-w-[1088px] mx-auto">
          <div key={pathname} className="animate-screenFade">
            <Outlet context={{ refreshUnread }} />
          </div>
        </div>
        {celebration}
      </div>
    );
  }

  // Same reasoning as the desktop branch: the gradient is on the full-width
  // element, the width cap is the child. Between 672px and the md breakpoint
  // the mobile shell is also narrower than the viewport, so it had the same
  // seam.
  return (
    <div className="min-h-screen" style={{ background: PAGE_BG }}>
      <div className="max-w-2xl mx-auto">
        <div key={pathname} className="animate-screenFade">
          <Outlet context={{ refreshUnread }} />
        </div>
        <BottomNav unreadCount={unreadCount} />
      </div>
      {celebration}
    </div>
  );
};

export default AppShell;
